/** Author: Codex CLI Agent // Generation: P0 event-flow hardening // Version: v3.2.242 // Modified: 2026-04-02T19:05:00-07:00 */
import { io, Socket } from 'socket.io-client';
import {
  ClientMessage,
  ServerMessage,
  ConnectionStatus,
  UserPreferences
} from '@types/websocket';
import { normalizeColor } from '../../utils/colorMapping';
import PlaybackScheduler, { PlaybackHandlers as VizHandlers, GameEvent as VizEvent } from '../../game/playback/PlaybackScheduler';
import ClientTrace from '../telemetry/ClientTrace';
import { COMBAT_TIMING_CONSTANTS, computeCombatPlannedDuration, computeMovePlannedDuration, computeUseItemPlannedDuration, computeCollectPlannedDuration } from '../../constants/combatTiming';
import { resolveSocketTarget } from './socketUrl';
import {
  transformAgentSpawnedPayload,
  transformResourceSpawnPayload,
  transformResourceCollectedPayload,
  transformSafeZoneUpdatedPayload,
  transformSafeZoneWarningPayload,
  transformSafeZoneContractionPayload
} from './transforms';
// import { FrontendTestLogger, FrontendEventCategory } from '@utils/TestLogger'; // Commented out - using console.log for E2E testing

interface WebSocketConfig {
  url: string;
  reconnectAttempts: number;
  reconnectDelay: number;
  heartbeatInterval: number;
  eventQueueSize: number;
  autoReconnect: boolean;
}

const DAMAGE_SOURCE_TTL_MS = 1500;
const MOVE_FALLBACK_PATH = 1;

const MOVE_EVENTS = new Set(['AGENT_MOVED']);
const COMBAT_EVENTS = new Set(['COMBAT_STARTED', 'COMBAT_DAMAGE_DEALT', 'COMBAT_ATTACK_MISSED', 'COMBAT_CRITICAL_HIT', 'COMBAT_ENDED', 'COMBAT_SIGNAL_STARTED', 'COMBAT_SIGNAL_ATTACK', 'COMBAT_SIGNAL_MISS', 'COMBAT_SIGNAL_HP', 'COMBAT_SIGNAL_ENDED']);
const META_EVENTS = new Set([
  'LLM_STRATEGY_ANALYZED',
  'LLM_PATH_PLANNED',
  'SCENARIO_STARTED',
  'SCENARIO_COMPLETED',
  'SCENARIO_ABORTED',
  // 资源清理应为即时状态变更：不参与播放闸门
  'RESOURCE_DESPAWNED'
]);
const PROBE_ENABLED = (() => {
  try {
    return typeof import.meta !== 'undefined' && import.meta.env?.VITE_PROBE === '1';
  } catch {
    return false;
  }
})();
const SAFEZONE_EVENTS = new Set(['SAFEZONE_DAMAGE_APPLIED', 'SAFEZONE_DAMAGEAPPLIED', 'SAFEZONE_TICK']);

type EventClassification = 'move' | 'combat' | 'combat_hp' | 'other' | 'meta';
type DamageSourceKind = 'combat' | 'safezone' | 'environment';

interface MoveWindowState {
  endAt: number;
  agentId: string;
  turn: number;
}

interface CombatWindowState {
  combatId: string;
  startAt: number;
  endAt?: number;
  attackCount: number;
  missCount: number;
  hasDeath: boolean;
  attackerId?: string;
  defenderId?: string;
}

interface DamageSourceEntry {
  source: DamageSourceKind;
  timestamp: number;
}

export class WebSocketService {
  private socket: Socket | null = null;
  private connectPromise: Promise<void> | null = null;
  private socketAuthToken: string | null = null;
  private config: WebSocketConfig;
  private messageHandlers: ((message: ServerMessage) => void)[] = [];
  private reconnectAttemptHandlers: (() => void)[] = [];
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private connectionStatus: ConnectionStatus;
  private messageQueue: ClientMessage[] = [];
  private isAuthenticated: boolean = false;
  private authCredentials: { username: string; password: string } | null = null;

  // 🆕 游戏事件处理扩展
  private gameEventHandlers: Map<string, Function[]> = new Map();
  private gameRegistry: any = null; // Phaser.Data.DataManager
  private mockMode: boolean = false;
  // 可视化回放：单一入口（可选开启）
  private playbackScheduler: PlaybackScheduler | null = null;
  private playbackEnabled: boolean = false;

  // 🆕 Registry增强功能 - Phase 3
  private registryChangeListeners: Map<string, Function[]> = new Map();
  private registryHistory: Map<string, { value: any; timestamp: number }[]> = new Map();
  private registrySnapshot: Map<string, any> = new Map();
  // 🔒 Bridge registration guard
  private bridgeRegistered: boolean = false;
  private bridgeHandlers: Array<{ backend: string; handler: (ev:any)=>void }> = [];
  // 🆕 Scene handshake dedupe (per room)
  private sceneHandshakeSent: Set<string> = new Set();
  private bridgeSocket: Socket | null = null;
  // v3.2.160: room → seqWatermark cursor
  private lastSeqWatermarkByRoom: Map<string, number> = new Map();
  // v3.2.183: Cache last authority snapshot for late listeners
  private lastSystemSnapshot: any | null = null;
  // MOVE 固定时长：记录 LLM_PATH_PLANNED 提供的步数
  private plannedMoveStepsByAgent: Map<string, { steps: number; recordedAt: number }> = new Map();
  // Agent颜色缓存：用于剧场展示真实史莱姆类型
  private agentColorById: Map<string, string> = new Map();
  private activeMoveWindow: MoveWindowState | null = null;
  private activeCombatWindow: CombatWindowState | null = null;
  private lastDamageSource: Map<string, DamageSourceEntry> = new Map();
  private pendingCombatBuffers: Map<string, { events: Array<{ type: string; payload: any }>; flushAt: number }> = new Map();
  private moveWindowTimer: NodeJS.Timeout | null = null;
  private customSocketListeners: Array<{ event: string; handler: (...args: any[]) => void }> = [];
  private routerSignalListener?: (ev: Event) => void;

  constructor(config?: Partial<WebSocketConfig>) {
    this.config = {
      // v3.2.146: Unify to VITE_WS_URL (fallback to BACKEND_URL/window global)
      url: (
        // Vite client env
        (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_WS_URL)
        // Jest/Node env fallback
        || (typeof process !== 'undefined' && (process.env as any)?.VITE_WS_URL)
        || (typeof process !== 'undefined' && (process.env as any)?.BACKEND_URL)
        // Legacy global override (dev tools)
        || (typeof window !== 'undefined' && (window as any).__ABR_WS_URL__)
        || 'http://localhost:8080'
      ) as string,
      reconnectAttempts: 5,
      reconnectDelay: 1000,
      heartbeatInterval: 30000,
      eventQueueSize: 1000,
      autoReconnect: true,
      ...config
    };

    this.connectionStatus = {
      connected: false,
      reconnectAttempts: 0,
      lastHeartbeat: new Date().toISOString()
    };

    if (typeof window !== 'undefined') {
      this.routerSignalListener = (event: Event) => {
        const detail = (event as CustomEvent)?.detail;
        if (!detail) return;
        const kind = String(detail.kind || '').toLowerCase();
        const payload = detail.payload || {};
        const typeMap: Record<string, string> = {
          started: 'COMBAT_SIGNAL_STARTED',
          damage: 'COMBAT_SIGNAL_ATTACK',
          miss: 'COMBAT_SIGNAL_MISS',
          ended: 'COMBAT_SIGNAL_ENDED'
        };
        const mappedType = typeMap[kind];
        if (!mappedType) return;
        const classification = this.classifyEvent(mappedType, payload);
        this.handleWindowPriming(mappedType, payload, classification, Date.now());
      };
      window.addEventListener('ABR_ROUTER_COMBAT_SIGNAL', this.routerSignalListener as EventListener);
    }
  }

  getConfig(): WebSocketConfig {
    return { ...this.config };
  }

  private async getStoredAuthToken(): Promise<string | null> {
    try {
      const { useAuthStore } = await import('/src/store/AuthStore');
      return useAuthStore.getState().token;
    } catch {
      return null;
    }
  }

  connect(url?: string): Promise<void> {
    if (url) {
      this.config.url = url;
    }

    if (this.isConnected()) {
      return Promise.resolve();
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }
    
    const candidates = this.buildCandidateUrls(this.config.url);
    let candidateIndex = 0;

    const tryConnect = (target: string): Promise<void> => new Promise(async (resolve, reject) => {
      try {
        // 防御：若已有旧socket，先彻底清理，避免重复监听残留
        if (this.socket) {
          try { this.socket.removeAllListeners(); } catch {}
          try { this.socket.disconnect(); } catch {}
          this.socket = null;
        }

        const token = await this.getStoredAuthToken();

        // 规范化：允许传入 http(s)/ws(s) 协议，最终交给 socket.io 处理
        const socketTarget = resolveSocketTarget(target);
        console.log('🌐 WebSocketService: connecting to', socketTarget.url, socketTarget.path ? `(path=${socketTarget.path})` : '', token ? '(with token)' : '(no token)');
        this.socket = io(socketTarget.url, {
          reconnection: this.config.autoReconnect,
          reconnectionAttempts: this.config.reconnectAttempts,
          reconnectionDelay: this.config.reconnectDelay,
          transports: ['polling'],
          ...(socketTarget.path ? { path: socketTarget.path } : {}),
          auth: {
            token: token || undefined
          }
        });

        try {
          this.customSocketListeners.forEach(({ event, handler }) => {
            try { this.socket?.on(event, handler); } catch {}
          });
        } catch {}

        this.socket.on('connect', async () => {
          this.config.url = target; // lock to the working URL
          this.socketAuthToken = token || null;
          console.log('🔌 WebSocket connected to:', target);
          try { ClientTrace.log('ws', 'ws-core', 'CONNECTED', { details: { url: target } }); } catch {}
          this.connectionStatus.connected = true;
          this.connectionStatus.reconnectAttempts = 0;

          // Test logging: Track WebSocket connection
          // FrontendTestLogger.log(FrontendEventCategory.WS, 'WEBSOCKET_CONNECTED', {
          //   url: target,
          //   timestamp: Date.now()
          // }); // Commented out - using console.log for E2E testing

          // Auto-authenticate if credentials are available
          if (this.authCredentials && !this.isAuthenticated) {
            try {
              await this.authenticate(this.authCredentials.username, this.authCredentials.password);
            } catch (error) {
              console.error('Auto-authentication failed:', error);
            }
          }

          // 绑定 roomId 供应给前端日志抓取器
          try {
            ClientTrace.setRoomSupplier(() => {
              try { return (window as any).__ABR_CURRENT_ROOM_ID__ || (require('@/store/RoomStore') as any).useRoomStore.getState().currentRoom?.id; } catch {}
              return undefined;
            });
          } catch {}

          // 后端→前端事件桥接（冒号风格 → 大写事件）
          try { this.setupBackendEventBridge(this.socket!); } catch (e) { console.warn('Backend event bridge setup failed:', (e as Error).message); }

          // Register business event listeners on every connect/reconnect
          // Listen for room:user_ready event to update participant ready status
          this.socket!.on('rooms:user_ready', async (data: { roomId: string; participant: any }) => {
            console.log('📢 User ready status updated:', data);
            const { useRoomStore } = await import('@store/RoomStore');
            const { handleUserReady } = useRoomStore.getState();
            handleUserReady(data.roomId, data.participant);
          });

          // Listen for rooms:user_configured event (completing configuration = ready)
          this.socket.on('rooms:user_configured', async (data: { roomId: string; participant: any }) => {
            console.log('📢 User configured (auto-ready):', data);
            const { useRoomStore } = await import('@store/RoomStore');
            const { handleUserReady } = useRoomStore.getState();
            handleUserReady(data.roomId, data.participant);
          });

          // ✅ Enhanced: Listen for user joined event to update participant list
          this.socket.on('rooms:user_joined', async (data: { roomId: string; participant: any }) => {
            console.log('👤 [ROOM] User joined:', data);
            const { useRoomStore } = await import('@store/RoomStore');
            const { handleUserJoined } = useRoomStore.getState();
            handleUserJoined(data.roomId, data.participant);
          });

          // ✅ Enhanced: Listen for user left event to update participant list
        this.socket.on('rooms:user_left', async (data: { roomId: string; userId: string }) => {
          console.log('👋 [ROOM] User left:', data);
          const { useRoomStore } = await import('@store/RoomStore');
          const { handleUserLeft } = useRoomStore.getState();
          handleUserLeft(data.roomId, data.userId);
        });

          // ✅ Enhanced: Listen for room created event to update lobby room list
          this.socket.on('rooms:created', async (room: any) => {
            console.log('🏠 [ROOM] New room created:', room);
            const { useRoomStore } = await import('@store/RoomStore');
            const { handleRoomCreated } = useRoomStore.getState();
            handleRoomCreated(room);
          });

          // ✅ Enhanced: Listen for room deleted event to update lobby room list
          this.socket.on('rooms:deleted', async (roomId: string) => {
            console.log('🗑️ [ROOM] Room deleted:', roomId);
            const { useRoomStore } = await import('@store/RoomStore');
            const { handleRoomDeleted } = useRoomStore.getState();
            handleRoomDeleted(roomId);
          });

          // 🆕 注入前端client trace上传器：将NDJSON通过WS上传到后端固定目录
          try {
            ClientTrace.setUploader(async (filename: string, content: string) => {
              try {
                const ack: any = await this.emit('client:trace:upload', { filename, content });
                return !!ack?.success;
              } catch (e) {
                console.warn('client:trace:upload failed', (e as any)?.message || e);
                return false;
              }
            });
          } catch {}

          // 🆕 v3.2.139: Listen for rooms:state_sync event to sync RoomStore
          this.socket.on('rooms:state_sync', async (data: { rooms: any[]; participants: any[] }) => {
            console.log('🔄 [ROOM] State sync received:', data);

            // Test logging: Track room state reception
            // FrontendTestLogger.log(FrontendEventCategory.ROOM, 'ROOM_STATE_RECEIVED', {
            //   roomCount: data.rooms?.length || 0,
            //   participantCount: data.participants?.length || 0
            // }); // Commented out - using console.log for E2E testing

            const { useRoomStore } = await import('@store/RoomStore');
            const { handleRoomStateSynced } = useRoomStore.getState();
            handleRoomStateSynced(data);
          });

          // 🆕 v3.2.146: Map loading events to UPPER_SNAKE_CASE for scene handlers
          this.socket.on('game:loading:started', (data: any) => {
            console.log('📡 [WS] game:loading:started received:', data);
            const payload = data && typeof data === 'object' && 'data' in data ? (data as any).data : data;
            this.triggerGameEvent('GAME_LOADING_STARTED', payload);
          });

          this.socket.on('game:loading:progress', (data: any) => {
            console.log('📡 [WS] game:loading:progress received:', data);
            const payload = data && typeof data === 'object' && 'data' in data ? (data as any).data : data;
            this.triggerGameEvent('GAME_LOADING_PROGRESS', payload);
          });

          this.socket.on('game:loading:completed', async (data: any) => {
            console.log('📡 [WS] game:loading:completed received:', data);
            const payload = data && typeof data === 'object' && 'data' in data ? (data as any).data : data;
            this.triggerGameEvent('GAME_LOADING_COMPLETED', payload);
            // 🆕 Fallback: auto-send scene:ready handshake if BattleScene not yet created
            try {
              const roomId: string | undefined = (await import('@store/RoomStore')).useRoomStore.getState().currentRoom?.id;
              if (roomId && !this.sceneHandshakeSent.has(roomId)) {
                setTimeout(() => {
                  try {
                    console.log(`🤝 [AUTO] Sending ui:scene:ready after loading:completed | roomId=${roomId}`);
                    this.sendSceneReady(roomId, 'BattleScene');
                    this.sceneHandshakeSent.add(roomId);
                  } catch (e) {
                    console.warn('AUTO scene:ready failed:', (e as Error).message);
                  }
                }, 50);
              }
            } catch {}
          });

          this.startHeartbeat();
          this.processQueuedMessages();
          resolve();
        });

        this.socket.on('connect_error', (error: Error) => {
          console.error('❌ WebSocket connection error to', target, ':', error.message);
          try { ClientTrace.log('ws', 'ws-core', 'CONNECT_ERROR', { details: { url: target, error: error.message } }); } catch {}
          this.connectionStatus.connected = false;
          // Fallback to next candidate (localhost only)
          const isLocal = /^https?:\/\/localhost:\d+$/i.test(target) || /^ws:\/\/localhost:\d+$/i.test(target);
          if (isLocal && candidateIndex < candidates.length - 1) {
            candidateIndex++;
            const next = candidates[candidateIndex];
            console.warn('↪️ Trying fallback WebSocket URL:', next);
            // small delay to avoid tight loop
            setTimeout(() => {
              tryConnect(next).then(resolve).catch(reject);
            }, 200);
          } else {
            reject(error);
          }
        });

        this.socket.on('disconnect', (reason: string) => {
          console.log(`WebSocket disconnected: ${reason}`);
          try { ClientTrace.log('ws', 'ws-core', 'DISCONNECTED', { details: { reason } }); } catch {}
          this.connectionStatus.connected = false;
          this.stopHeartbeat();
          // Bridge cleanup handled in setupBackendEventBridge on reconnect

          if (this.config.autoReconnect) {
            this.handleReconnect();
          }
        });

        this.socket.on('message', (message: ServerMessage) => {
          this.handleServerMessage(message);
        });

        // Listen for backend welcome message
      this.socket.on('connected', (data: any) => {
        console.log('📡 Backend welcome message:', data);
        try { ClientTrace.log('receive', 'ws-core', 'BACKEND_CONNECTED', { details: data }); } catch {}
      });

      } catch (error) {
        reject(error);
      }
    });

    this.connectPromise = tryConnect(this.config.url).then(
      (result) => {
        this.connectPromise = null;
        return result;
      },
      (error) => {
        this.connectPromise = null;
        throw error;
      }
    );

    return this.connectPromise;
  }

  // Build a list of candidate URLs for localhost fallback (8080..8089)
  private buildCandidateUrls(primary: string): string[] {
    const list: string[] = [];
    try {
      const m = primary.match(/^(https?:|ws:)\/\/localhost:(\d+)(\/.*)?$/i);
      if (!m) return [primary];
      const basePort = parseInt(m[2], 10);
      const scheme = m[1].startsWith('ws') ? 'ws' : 'http';
      const suffix = m[3] || '';
      for (let p = basePort; p < basePort + 10; p++) {
        list.push(`${scheme}://localhost:${p}${suffix}`);
      }
      return list;
    } catch {
      return [primary];
    }
  }

  // ================= Backend → Frontend Event Bridge =================
  /**
   * 将后端通过 Socket.IO 广播的冒号风格事件（如 'resource:spawned'）
   * 映射为前端内部总线使用的大写事件（如 'RESOURCE_SPAWNED'），并解包 GameEvent.data。
   * 仅做桥接与轻量日志，动画实际由 BattleScene/Engine 统一处理。
   */
  private setupBackendEventBridge(socket: Socket): void {
    // 防止跨连接重复注册：若已有旧socket注册，先卸载旧监听
    if (this.bridgeRegistered && this.bridgeSocket && this.bridgeHandlers.length) {
      try {
        this.bridgeHandlers.forEach(({ backend, handler }) => {
          try { this.bridgeSocket!.off(backend, handler as any); } catch {}
        });
      } catch {}
      this.bridgeHandlers = [];
      this.bridgeRegistered = false;
      this.bridgeSocket = null;
    }

      const bridge: Array<{ backend: string; internal: string; hint?: string; transform?: (p: any) => any }>= [
      // Game lifecycle
      { backend: 'game:initialized', internal: 'GAME_INITIALIZED' },
      { backend: 'game:started', internal: 'GAME_STARTED' },
      { backend: 'game:ended', internal: 'GAME_ENDED', hint: 'theater/scene teardown' },
      { backend: 'game:paused', internal: 'GAME_PAUSED' },
      { backend: 'game:resumed', internal: 'GAME_RESUMED' },
      { backend: 'game:reset', internal: 'GAME_RESET' },

      // Turn system
      { backend: 'turn:started', internal: 'TURN_STARTED' },
      { backend: 'turn:ended', internal: 'TURN_ENDED' },
      { backend: 'turn:phaseChanged', internal: 'TURN_PHASE_CHANGED' },
      { backend: 'agent:turnStarted', internal: 'AGENT_TURN_STARTED' },
      { backend: 'agent:turnEnded', internal: 'AGENT_TURN_ENDED' },

      // Agent lifecycle & actions
      { backend: 'agent:spawned', internal: 'AGENT_SPAWNED', hint: 'idle@spawn', transform: (p:any)=> transformAgentSpawnedPayload(p) },
      { backend: 'agent:eliminated', internal: 'AGENT_ELIMINATED', hint: 'die animation' },
      { backend: 'agent:statusChanged', internal: 'AGENT_STATUS_CHANGED' },
      { backend: 'agent:moved', internal: 'AGENT_MOVED', hint: 'walk/move animation', transform: (p:any)=> {
        if (!p) return p;
        const to = p.to || p.toPosition || p.position;
        const from = p.from || p.fromPosition;
        return { ...p, toPosition: to, position: to, fromPosition: from };
      } },
      { backend: 'agent:healthChanged', internal: 'AGENT_HEALTH_CHANGED' },

      // Combat
      { backend: 'combat:started', internal: 'COMBAT_STARTED', hint: 'theater open / prep' },
      { backend: 'combat:damageDealt', internal: 'COMBAT_DAMAGE_DEALT', hint: 'atk + hurt' },
      { backend: 'combat:attackMissed', internal: 'COMBAT_ATTACK_MISSED', hint: 'atk + miss' },
      { backend: 'combat:criticalHit', internal: 'COMBAT_CRITICAL_HIT', hint: 'atk + critical VFX' },
      { backend: 'combat:ended', internal: 'COMBAT_ENDED', hint: 'theater close' },

      // Resource/Inventory
      { backend: 'resource:spawned', internal: 'RESOURCE_SPAWNED', transform: (p:any)=> transformResourceSpawnPayload(p) },
      { backend: 'resource:collected', internal: 'RESOURCE_COLLECTED', hint: 'weapon→pickup feedback', transform: (p:any)=> transformResourceCollectedPayload(p) },
      { backend: 'resource:despawned', internal: 'RESOURCE_DESPAWNED' },
      { backend: 'item:used', internal: 'ITEM_USED' },
      { backend: 'item:consumed', internal: 'ITEM_CONSUMED' },
      { backend: 'item:dropped', internal: 'ITEM_DROPPED' },
      { backend: 'weapon:equipped', internal: 'WEAPON_EQUIPPED', hint: 'equip feedback' },
      { backend: 'weapon:durabilityChanged', internal: 'WEAPON_DURABILITY_CHANGED' },
      { backend: 'weapon:broken', internal: 'WEAPON_BROKEN', hint: 'break VFX' },

      // Environment / SafeZone / Map
      { backend: 'safeZone:updated', internal: 'SAFEZONE_UPDATED', transform: (p:any)=> transformSafeZoneUpdatedPayload(p) },
      { backend: 'safeZone:warning', internal: 'SAFEZONE_WARNING', transform: (p:any)=> transformSafeZoneWarningPayload(p) },
      { backend: 'safeZone:contractionStarted', internal: 'SAFEZONE_CONTRACTION_STARTED', transform: (p:any)=> transformSafeZoneContractionPayload(p) },
      { backend: 'safeZone:contractionCompleted', internal: 'SAFEZONE_CONTRACTION_COMPLETED', transform: (p:any)=> transformSafeZoneContractionPayload(p, true) },
      { backend: 'safeZone:damageApplied', internal: 'SAFEZONE_DAMAGE_APPLIED' },
      { backend: 'environment:damage', internal: 'ENVIRONMENT_DAMAGE' },
      { backend: 'map:generated', internal: 'MAP_GENERATED' },
      { backend: 'terrain:changed', internal: 'TERRAIN_CHANGED' },
      // UI / Test overlays
      { backend: 'ui:heatmap', internal: 'UI_HEATMAP', transform: (p:any)=>{ try { window.dispatchEvent(new CustomEvent('ABR_UI_HEATMAP_MARK', { detail: { cells: p?.cells||[], agentId: p?.agentId } })); } catch {}; return p; } },

      // LLM decisions
      { backend: 'llm:pathPlanned', internal: 'LLM_PATH_PLANNED', hint: 'planning overlay (optional)' },
      { backend: 'llm:optionsGenerated', internal: 'LLM_OPTIONS_GENERATED' },
      { backend: 'llm:optionEvaluated', internal: 'LLM_OPTION_EVALUATED' },
      { backend: 'llm:decisionMade', internal: 'LLM_DECISION_MADE' },
      { backend: 'llm:strategyAnalyzed', internal: 'LLM_STRATEGY_ANALYZED', hint: 'thinking highlight (optional)' },
      { backend: 'llm:perceptionUpdated', internal: 'LLM_PERCEPTION_UPDATED' },
      { backend: 'llm:memoryUpdated', internal: 'LLM_MEMORY_UPDATED' },
      { backend: 'llm:errorRecovered', internal: 'LLM_ERROR_RECOVERED' },

      // System / Stats / Analytics（按需渲染）
      { backend: 'system:error', internal: 'SYSTEM_ERROR' },
      { backend: 'system:warning', internal: 'SYSTEM_WARNING' },
      { backend: 'system:stateSync', internal: 'SYSTEM_STATE_SYNC' },
      { backend: 'system:heartbeat', internal: 'SYSTEM_HEARTBEAT' },
      { backend: 'system:performanceMetric', internal: 'SYSTEM_PERFORMANCE_METRIC' },
      { backend: 'stats:turnComplete', internal: 'STATS_TURN_COMPLETE' },
      { backend: 'stats:gameComplete', internal: 'STATS_GAME_COMPLETE' },
      { backend: 'stats:agentPerformance', internal: 'STATS_AGENT_PERFORMANCE' },
      { backend: 'analytics:userAction', internal: 'ANALYTICS_USER_ACTION' },

      // 👁️ P2.1: Spectator System
      { backend: 'spectator:joined', internal: 'SPECTATOR_JOINED', hint: 'spectator count update' },
      { backend: 'spectator:left', internal: 'SPECTATOR_LEFT', hint: 'spectator count update' },
    ];

    const handler = (map: {internal:string; hint?:string; transform?: (p:any)=>any}) => (ev: any) => {
      const wrapper = ev && typeof ev === 'object' && 'data' in ev ? (ev as any) : null;
      let payload = wrapper ? wrapper.data : ev;
      if (typeof map.transform === 'function') {
        try { payload = map.transform(payload); } catch (e) { console.warn(`Transform failed for ${map.internal}:`, (e as Error).message); }
      }
      // 传递后端事件序列号，便于前端去重/排序（不影响业务逻辑）
      try { if (wrapper && typeof wrapper.sequence === 'number') { (payload as any).__seq = wrapper.sequence; } } catch {}
      // 兼容 plain object：若顶层存在 sequence，也注入 __seq
      try { if (typeof (ev as any)?.sequence === 'number' && typeof (payload as any)?.__seq !== 'number') { (payload as any).__seq = (ev as any).sequence; } } catch {}
      // 兼容 payload 平铺：若 payload.sequence 存在也注入 __seq（后端最小改动：顶层平铺 sequence/turnNumber）
      try { if (typeof (payload as any)?.sequence === 'number' && typeof (payload as any)?.__seq !== 'number') { (payload as any).__seq = (payload as any).sequence; } } catch {}
      // 归一化 combatId：sessionId/cid/combat_id → combatId（避免 Router 因缺 combatId 丢弃）
      try {
        const p: any = payload || {};
        if (p.combatId == null) { p.combatId = p.sessionId ?? p.cid ?? p.combat_id ?? (ev as any)?.sessionId ?? (ev as any)?.cid ?? (ev as any)?.combat_id; }
      } catch {}

      // 🔍 PROBE: 记录战斗桥接是否携带 seq/combatId（便于现场确认根因）
      try {
        if (map.internal.startsWith('COMBAT_')) {
          const p: any = payload || {};
          const hasSeq = typeof p.__seq === 'number';
          const hasCid = !!p.combatId;
          const seqVal = hasSeq ? p.__seq : undefined;
          const cidVal = hasCid ? p.combatId : undefined;
          ClientTrace.log('receive','ws-bridge-probe','COMBAT_BRIDGE_IN', { details: { evt: map.internal, hasSeq, seq: seqVal, hasCombatId: hasCid, combatId: cidVal } });
        }
      } catch {}

      // v3.2.160: 序列水位过滤（注意：为避免观战场景中快照提升水位导致后续事件被误判为重复，这里不再用快照的水位提升阈值）
      try {
        const roomId = (wrapper?.context?.gameId) || (payload?.roomId) || (payload?.gameId);
        const seq = typeof (payload as any)?.__seq === 'number'
          ? (payload as any).__seq
          : undefined;
        const isLoadingEvent = map.internal.startsWith('GAME_LOADING_');
        const isSystemSnapshot = map.internal === 'SYSTEM_STATE_SYNC';
        const isMapGenerated = map.internal === 'MAP_GENERATED';
        // 仅针对运行期事件执行“去重”过滤；不再使用快照携带的水位提升阈值（避免观战模式中后续事件被丢弃）
        if (!isLoadingEvent && !isSystemSnapshot && roomId && typeof seq === 'number') {
          const watermark = this.lastSeqWatermarkByRoom.get(roomId);
          if (typeof watermark === 'number' && seq <= watermark) { return; }
          // 提升本地水位（按事件自然递增）
          const next = Math.max(watermark ?? -1, seq);
          this.lastSeqWatermarkByRoom.set(roomId, next);
        }
        // 收到快照时，清除本地水位，允许后续事件全部通过（避免新观战 socket 的序列从 1 开始而被误丢弃）
        if (isSystemSnapshot) {
          const rid = (payload?.turn?.gameId) || (payload?.roomId) || (wrapper?.context?.gameId);
          if (rid && this.lastSeqWatermarkByRoom.has(rid)) { this.lastSeqWatermarkByRoom.delete(rid); }
        }
      } catch (e) { console.warn('seqWatermark filter error:', (e as Error).message); }
      try {
        // 🔍 DEBUG: Log every game event received from backend
        console.log(`📥 [WebSocketService] Received event: ${map.internal}`, payload);
        try {
          const agentId = (payload && (payload.agentId || payload.id || (payload.agent && payload.agent.id))) || undefined;
          const turn = (payload && (payload.turnNumber || payload.turn)) || undefined;
          ClientTrace.log('receive', 'ws-bridge', map.internal, { agentId, turn, details: undefined });
        } catch {}
        // 可选：在桥接层记录动画提示（实际动画由 Engine 驱动）
        if (map.hint) console.log(`🎬 Bridge Animation Hint: ${map.internal} → ${map.hint}`);
        this.triggerGameEvent(map.internal, payload);
      } catch (e) {
        console.error(`Bridge dispatch failed for ${map.internal}:`, (e as Error).message);
      }
    };

    bridge.forEach(map => {
      try {
        const h = handler(map);
        socket.on(map.backend, h);
        this.bridgeHandlers.push({ backend: map.backend, handler: h });
      }
      catch (e) { console.warn(`Bridge listener failed: ${map.backend}`, (e as Error).message); }
    });
    this.bridgeRegistered = true;
    this.bridgeSocket = socket;
    // 🔍 DEBUG: Log event bridge initialization completion
    console.log(`✅ [WebSocketService] Event bridge initialized - ${bridge.length} event types registered`);
    try { ClientTrace.log('receive', 'ws-bridge', 'BRIDGE_READY', { details: { count: bridge.length } }); } catch {}
  }

  // v3.2.160: Client-side API to request snapshot on timeout
  requestSnapshot(roomId: string): void {
    try { this.socket?.emit('request:snapshot', { roomId }); } catch {}
  }

  // 纯函数已抽离至 transforms.ts

  // 尝试从Registry读取tileSize（如无则返回undefined）
  private getTileSizeFromRegistry(): number | undefined {
    try {
      if (!this.gameRegistry) return undefined;
      const keys = ['tileSize', 'gridSize', 'TILE_SIZE', 'GRID_SIZE'];
      for (const k of keys) {
        const v = this.gameRegistry.get(k);
        if (typeof v === 'number' && v > 0) return v;
      }
    } catch {}
    return undefined;
  }

  disconnect(): void {
    this.connectPromise = null;
    this.socketAuthToken = null;
    if (this.socket) {
      try {
        this.customSocketListeners.forEach(({ event, handler }) => {
          try { this.socket?.off(event, handler); } catch {}
        });
      } catch {}
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionStatus.connected = false;
    this.isAuthenticated = false;
    this.authCredentials = null;
    this.stopHeartbeat();
    console.log('WebSocket disconnected');
  }
  
  logout(): void {
    // Clear authentication state and disconnect
    this.isAuthenticated = false;
    this.authCredentials = null;
    this.disconnect();
    console.log('User logged out');
  }

  onServerEvent(eventName: string, handler: (...args: any[]) => void): void {
    if (!eventName || typeof handler !== 'function') return;
    this.customSocketListeners.push({ event: eventName, handler });
    try { this.socket?.on(eventName, handler); } catch {}
  }

  offServerEvent(eventName: string, handler: (...args: any[]) => void): void {
    if (!eventName || typeof handler !== 'function') return;
    this.customSocketListeners = this.customSocketListeners.filter(l => !(l.event === eventName && l.handler === handler));
    try { this.socket?.off(eventName, handler); } catch {}
  }
  
  async authenticate(username: string, password: string): Promise<any> {
    // Store credentials for auto-reconnect
    this.authCredentials = { username, password };

    if (!this.socket?.connected) {
      // If not connected, connect first
      await this.connect();
    }

    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not initialized'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Authentication timeout'));
      }, 5000);

      this.socket.emit('auth:login', { username, password }, (response: any) => {
        clearTimeout(timeout);

        if (response && response.success) {
          console.log('✅ WebSocket authenticated successfully');
          this.isAuthenticated = true;
          resolve(response); // ✅ 返回完整response（包含token）
        } else {
          console.error('❌ WebSocket authentication failed:', response);
          this.isAuthenticated = false;
          // Extract error message properly to avoid [object Object] display
          const errorMsg = typeof response?.error === 'string'
            ? response.error
            : (response?.error?.message || 'Authentication failed');
          reject(new Error(errorMsg));
        }
      });
    });
  }

  isConnected(): boolean {
    return this.connectionStatus.connected && this.socket?.connected === true;
  }

  private async ensureSocketAuthState(): Promise<void> {
    const token = await this.getStoredAuthToken();
    const expectedToken = token || null;
    const needsTokenReconnect = !!expectedToken && this.socketAuthToken !== expectedToken;

    if (needsTokenReconnect && (this.socket || this.connectPromise)) {
      this.disconnect();
    }

    if (!this.isConnected()) {
      await this.connect();
    }
  }

  async emit<T = any>(event: string, data?: any): Promise<T> {
    await this.ensureSocketAuthState();
    
    if (!this.isAuthenticated && this.authCredentials) {
      await this.authenticate(this.authCredentials.username, this.authCredentials.password);
    }
    
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not initialized'));
        return;
      }
      
      const timeout = setTimeout(() => {
        reject(new Error(`Timeout waiting for response to ${event}`));
      }, 10000);
      
      console.log(`🚀 WebSocket emitting: ${event}`, data);
      try {
        const roomId = (typeof window !== 'undefined' && (window as any).__ABR_CURRENT_ROOM_ID__) || undefined;
        ClientTrace.log('ws', 'ws-core', `EMIT:${event}`, { roomId, details: (data && typeof data === 'object') ? { keys: Object.keys(data).slice(0,6) } : undefined });
      } catch {}
      
      if (data !== undefined) {
        this.socket.emit(event, data, (response: T) => {
          clearTimeout(timeout);
          console.log(`✅ WebSocket response for ${event}:`, response);
          try { ClientTrace.log('ws', 'ws-core', `ACK:${event}`, { details: (response as any) }); } catch {}
          resolve(response);
        });
      } else {
        this.socket.emit(event, (response: T) => {
          clearTimeout(timeout);
          console.log(`✅ WebSocket response for ${event}:`, response);
          try { ClientTrace.log('ws', 'ws-core', `ACK:${event}`, { details: (response as any) }); } catch {}
          resolve(response);
        });
      }
    });
  }
  
  sendMessage(eventType: string, data: any): Promise<any> {
    // Backward compatibility - redirect to emit
    return this.emit(eventType, data);
  }

  /**
   * Google 一键登录：通过 Socket 发送 idToken 到后端
   */
  async authenticateWithGoogle(idToken: string): Promise<any> {
    // Ensure socket connected
    if (!this.isConnected()) {
      await this.connect();
    }
    return new Promise((resolve, reject) => {
      if (!this.socket) { reject(new Error('Socket not initialized')); return; }
      const timeout = setTimeout(() => reject(new Error('Google auth timeout')), 15000); // Increased from 8s to 15s for network latency tolerance
      this.socket.emit('auth:google', { idToken }, (response: any) => {
        clearTimeout(timeout);
        resolve(response);
      });
    });
  }

  /**
   * 🆕 Scene Handshake Protocol - 发送ui:scene:ready握手信号
   * 触发时机: BattleScene.create()完成监听器绑定后
   * Backend响应: system:stateSync → game:started → 开启gameplay事件通道
   */
  sendSceneReady(roomId: string, sceneName: string = 'BattleScene'): void {
    if (!this.socket || !this.socket.connected) {
      console.error('❌ Cannot send scene:ready - socket not connected');
      return;
    }

    // 🔍 DEBUG: Enhanced scene ready handshake logging
    console.log(`🤝 ======================================`);
    console.log(`📤 [WebSocketService] Sending ui:scene:ready handshake`);
    console.log(`   Scene: ${sceneName}`);
    console.log(`   Room ID: ${roomId}`);
    console.log(`   Socket Connected: ${this.socket.connected}`);
    console.log(`🤝 ======================================`);
    this.socket.emit('ui:scene:ready', { roomId, scene: sceneName });
    try {
      // Guard: mark handshake sent to avoid duplicate auto-send on game:loading:completed
      this.sceneHandshakeSent.add(roomId);
      ClientTrace.log('ws','ws-bridge','SCENE_READY_SENT', { details: { roomId, scene: sceneName } });
    } catch {}
  }

  // Legacy method for backward compatibility
  sendMessageLegacy(message: ClientMessage): void {
    if (!message.timestamp) {
      message.timestamp = new Date().toISOString();
    }

    if (this.isConnected() && this.socket) {
      this.socket.emit('message', message);
      console.log('WebSocket message sent - placeholder mode:', message.type);
    } else {
      // Queue message for later sending
      if (this.messageQueue.length < this.config.eventQueueSize) {
        this.messageQueue.push(message);
        console.log('WebSocket message queued - placeholder mode:', message.type);
      }
    }
  }

  onMessage(handler: (message: ServerMessage) => void): void {
    this.messageHandlers.push(handler);
  }

  onReconnectAttempt(handler: () => void): void {
    this.reconnectAttemptHandlers.push(handler);
  }

  subscribe(eventTypes: string[]): void {
    const message: ClientMessage = {
      type: 'subscribe',
      payload: { eventTypes },
      timestamp: new Date().toISOString()
    };
    this.sendMessageLegacy(message);
  }

  unsubscribe(eventTypes: string[]): void {
    const message: ClientMessage = {
      type: 'unsubscribe',
      payload: { eventTypes },
      timestamp: new Date().toISOString()
    };
    this.sendMessageLegacy(message);
  }

  subscribeToGame(gameId: string): void {
    const message: ClientMessage = {
      type: 'subscribe',
      payload: { gameId },
      timestamp: new Date().toISOString()
    };
    this.sendMessageLegacy(message);
  }

  unsubscribeFromGame(gameId: string): void {
    const message: ClientMessage = {
      type: 'unsubscribe',
      payload: { gameId },
      timestamp: new Date().toISOString()
    };
    this.sendMessageLegacy(message);
  }

  startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing timer
    
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected()) {
        const heartbeatMessage: ClientMessage = {
          type: 'heartbeat',
          payload: {},
          timestamp: new Date().toISOString()
        };
        this.sendMessageLegacy(heartbeatMessage);
        this.connectionStatus.lastHeartbeat = new Date().toISOString();
      }
    }, this.config.heartbeatInterval);
  }

  stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  getConnectionStatus(): ConnectionStatus {
    return { ...this.connectionStatus };
  }

  private handleServerMessage(message: ServerMessage): void {
    console.log('WebSocket message received - placeholder mode:', message.type);
    
    // Send ACK if required
    if (message.requiresAck && message.sequenceId) {
      const ackMessage: ClientMessage = {
        type: 'ack',
        payload: { sequenceId: message.sequenceId },
        timestamp: new Date().toISOString()
      };
      this.sendMessageLegacy(ackMessage);
    }

    // Notify all handlers
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  private handleReconnect(): void {
    this.connectionStatus.reconnectAttempts++;
    this.reconnectAttemptHandlers.forEach(handler => {
      try {
        handler();
      } catch (error) {
        console.error('Error in reconnect attempt handler:', error);
      }
    });
  }

  private processQueuedMessages(): void {
    const messages = [...this.messageQueue];
    this.messageQueue = [];
    
    messages.forEach(message => {
      this.sendMessageLegacy(message);
    });
    
    if (messages.length > 0) {
      console.log(`Processed ${messages.length} queued messages - placeholder mode`);
    }
  }

  // 🆕 游戏事件监听器管理
  onGameEvent(eventType: string, handler: Function): void {
    if (!this.gameEventHandlers.has(eventType)) {
      this.gameEventHandlers.set(eventType, []);
    }
    this.gameEventHandlers.get(eventType)!.push(handler);
  }

  // 🆕 添加on方法作为onGameEvent的别名 (兼容性支持)
  on(eventType: string, handler: Function): void {
    this.onGameEvent(eventType, handler);
    // v3.2.183: late listener immediate replay for SYSTEM_STATE_SYNC
    if (eventType === 'SYSTEM_STATE_SYNC' && this.lastSystemSnapshot) {
      try { setTimeout(() => { try { handler(this.lastSystemSnapshot); } catch {} }, 0); } catch {}
    }
  }

  offGameEvent(eventType: string, handler: Function): void {
    const handlers = this.gameEventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
        if (handlers.length === 0) {
          this.gameEventHandlers.delete(eventType);
        }
      }
    }
  }

  // 🆕 添加off方法作为offGameEvent的别名 (兼容性支持)
  off(eventType: string, handler: Function): void {
    this.offGameEvent(eventType, handler);
  }

  getGameEventHandlerCount(eventType: string): number {
    return this.gameEventHandlers.get(eventType)?.length || 0;
  }

  triggerGameEvent(eventType: string, data: any, opts?: { skipGate?: boolean }): void {
    // 自动更新Registry状态
    this.autoUpdateRegistryState(eventType, data);
    // 捕获 agent 颜色信息（剧场外观一致）
    this.captureAgentColors(eventType, data);
    // v3.2.183: cache last snapshot
    if (eventType === 'SYSTEM_STATE_SYNC') {
      this.lastSystemSnapshot = data;
      try {
        const currentPhase = this.gameRegistry?.get?.('gamePhase');
        const turn = this.gameRegistry?.get?.('currentTurn');
        ClientTrace.log('receive','ws-bridge','SNAPSHOT_RX', { turn, details: { phase: currentPhase } });
        if (currentPhase === 'RUNNING') {
          ClientTrace.log('receive','ws-bridge','SNAPSHOT_DURING_RUNNING', { turn, details: { hint: 'unexpected snapshot while running' } });
        }
      } catch {}
    }
    
    // 通知所有注册的处理器（状态先行）
    const isInternalEvent = !!(data && data.__viz_internal);

    // 内部事件：对下一行动者的高亮（AGENT_TURN_STARTED）也应遵守移动/战斗闸门
    // 避免上一名Agent的移动尚未完成时光标/镜头过早切换
    if (isInternalEvent && (eventType === 'AGENT_TURN_STARTED' || eventType === 'TURN_STARTED') && !opts?.skipGate) {
      try {
        const classification = this.classifyEvent(eventType, data);
        const wait = this.computeGateDelayIfNeeded(eventType, data, classification, Date.now());
        if (wait > 0) {
          setTimeout(() => {
            try { this.triggerGameEvent(eventType, data, { skipGate: true }); } catch {}
          }, wait);
          return;
        }
      } catch {}
    }
    let cachedClassification: EventClassification | null = null;
    const needsClassification = this.playbackEnabled && this.playbackScheduler;
    if (needsClassification) {
      cachedClassification = this.classifyEvent(eventType, data);
    }
    // 处理器闸门：仅排除纯 combat 信号，combat_hp 也需要排队，避免HP早于剧场
    const shouldGateHandlers = this.playbackEnabled && this.playbackScheduler && !opts?.skipGate && !isInternalEvent
      && cachedClassification !== 'combat';
    if (shouldGateHandlers) {
      const wait = this.computeGateDelayIfNeeded(eventType, data, cachedClassification!, Date.now());
      if (wait > 0) {
        try { ClientTrace.log('playback','playback','BUSINESS_HANDLER_DELAY', { details: { event: eventType, plannedMs: wait } }); } catch {}
        setTimeout(() => {
          try { this.triggerGameEvent(eventType, data, { ...(opts || {}), skipGate: true }); } catch (e) { console.warn('Delayed triggerGameEvent failed', e); }
        }, wait);
        return;
      }
    }

    const handlers = this.gameEventHandlers.get(eventType) || [];
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('Error in game event handler:', error);
      }
    });

    // Detect anomalous GAME_STARTED during running
    try {
      if (eventType === 'GAME_STARTED') {
        const phase = this.gameRegistry?.get?.('gamePhase');
        if (phase === 'RUNNING') {
          ClientTrace.log('receive','ws-bridge','GAME_STARTED_REENTRY', { details: { hint: 'received GAME_STARTED while already RUNNING' } });
        }
      }
    } catch {}

    // 可视化回放：将可视化相关事件转入 PlaybackScheduler 串行执行
    if (this.playbackEnabled && this.playbackScheduler) {
      const classification = cachedClassification ?? this.classifyEvent(eventType, data);
      if (opts?.skipGate) {
        if (classification !== 'move' && this.activeMoveWindow) {
          this.endMoveWindow();
        }
        if (classification !== 'combat' && classification !== 'combat_hp' && this.activeCombatWindow) {
          this.activeCombatWindow = null;
        }
      }
      const now = Date.now();

      if (!isInternalEvent) {
        if (!opts?.skipGate && (classification === 'combat' || classification === 'combat_hp') && this.bufferCombatEventIfNeeded(eventType, data)) {
          return;
        }
        if (eventType === 'LLM_PATH_PLANNED' && data?.agentId) {
          const steps = this.deriveStepsFromPathPlan(data);
          if (steps > 0) this.recordPlannedMoveSteps(String(data.agentId), steps);
          if (PROBE_ENABLED) {
            this.logProbe('LLM_PATH_PLANNED', {
              agentId: data?.agentId,
              pathLength: data?.pathLength,
              derivedSteps: steps
            });
          }
        }
        if (PROBE_ENABLED && eventType === 'COMBAT_STARTED') {
          const attackerId = data?.attackerId ?? data?.attacker;
          const defenderId = data?.defenderId ?? data?.defender;
          this.logProbe('COMBAT_STARTED', {
            attackerId,
            defenderId,
            rawAttackerColor: data?.attackerColor,
            rawDefenderColor: data?.defenderColor,
            cacheAttackerColor: attackerId ? this.lookupAgentColor(attackerId) : undefined,
            cacheDefenderColor: defenderId ? this.lookupAgentColor(defenderId) : undefined
          });
        }
        if (PROBE_ENABLED && eventType === 'GAME_ENDED') {
          const winners = Array.isArray(data?.winners) ? data.winners : (data?.winner ? [data.winner] : []);
          this.logProbe('GAME_ENDED', {
            reason: data?.reason,
            winnersCount: winners.length,
            winners
          });
        }
        this.updateDamageSource(eventType, data, classification, now);
        this.handleWindowPriming(eventType, data, classification, now);
        // 单闸：若需要在“窗口结束”前延后本事件播放，则直接延迟再进入本方法，而不是向队列塞 WAIT 任务
        if (!opts?.skipGate) {
          const wait = this.computeGateDelayIfNeeded(eventType, data, classification, now);
          if (wait > 0) {
            // 延后注入当前事件，避免在 Scheduler 队列中插入 WAIT_FIXED_* 任务
            setTimeout(() => {
              try { this.triggerGameEvent(eventType, data, { skipGate: true }); } catch {}
            }, wait);
            return; // 提前返回，不再将当前事件加入播放队列
          }
        }
      }

      const mapped = this.mapToVizEvent(eventType, data);
      if (mapped) {
        try { this.playbackScheduler.enqueue(mapped as VizEvent); } catch {}
      }

      const canApplyFixedLag = !this.activeMoveWindow && !this.activeCombatWindow;
      if (canApplyFixedLag && !isInternalEvent && (eventType === 'ITEM_USED' || eventType === 'ITEM_CONSUMED')) {
        try {
          const planned = computeUseItemPlannedDuration();
          if (planned > 0) this.playbackScheduler.enqueueFixedDelay(planned, { plannedMs: planned });
        } catch {}
      }
      if (canApplyFixedLag && !isInternalEvent && eventType === 'RESOURCE_COLLECTED') {
        try {
          const planned = computeCollectPlannedDuration();
          if (planned > 0) this.playbackScheduler.enqueueFixedDelay(planned, { plannedMs: planned });
        } catch {}
      }

      // 统一总线：内部事件喂 Router，并维护 readiness store
      try {
        const isCombat = (
          eventType === 'COMBAT_STARTED' ||
          eventType === 'COMBAT_DAMAGE_DEALT' ||
          eventType === 'COMBAT_ATTACK_MISSED' ||
          eventType === 'COMBAT_ENDED' ||
          eventType === 'COMBAT_CRITICAL_HIT'
        );
        if (isInternalEvent && isCombat) {
          import('../../game/animation/combat/CombatEventRouter').then(({ CombatEventRouter }) => {
            try {
              const router = (CombatEventRouter as any).peekInstance?.() || null;
              const bound = typeof window !== 'undefined' ? !!(window as any).__ABR_CR_BOUND : false;
              if (router && bound) {
                const feedType = eventType === 'COMBAT_CRITICAL_HIT' ? 'COMBAT_DAMAGE_DEALT' : eventType;
                // 补充剧场颜色：若缺失则从已捕获的agent颜色中补齐，避免默认绿色
                const rawPayload = eventType === 'COMBAT_CRITICAL_HIT'
                  ? { ...(data || {}), isCritical: true }
                  : (data || {});
                const attackerId = rawPayload?.attackerId ?? rawPayload?.attacker;
                const defenderId = rawPayload?.defenderId ?? rawPayload?.defender;
                const enriched = { ...rawPayload };
                try {
                  if (attackerId && !enriched.attackerColor) enriched.attackerColor = this.lookupAgentColor(attackerId);
                  if (defenderId && !enriched.defenderColor) enriched.defenderColor = this.lookupAgentColor(defenderId);
                } catch {}
                this.logCombatColorMapping('internal-feed', attackerId, defenderId, enriched);
                (router as any).feed(feedType, enriched);
              }
            } catch {}
          }).catch(()=>{});
        }
        if (isInternalEvent && eventType === 'AGENT_MOVED' && data?.agentId) {
          try { (require('../playback/ReadinessStore') as any).default.markMove(String(data.agentId), now); } catch {}
        }
        if ((eventType === 'AGENT_TURN_STARTED' || eventType === 'TURN_STARTED') && data?.agentId) {
          try { (require('../playback/ReadinessStore') as any).default.markTurnStart(String(data.agentId), now); } catch {}
        }
      } catch {}
    }

    // P0-2: 淘汰/零步移动的 ACK 兜底（统一本地派发，幂等）
    try {
      if (eventType === 'AGENT_ELIMINATED' && data?.agentId) {
        const aid = String(data.agentId);
        if (typeof window !== 'undefined') {
          const ts = Date.now();
          // 单步与全步完成信号，供 PlaybackScheduler/GamePage 等等待者收口
          window.dispatchEvent(new CustomEvent('ABR_MOVE_DONE', { detail: { agentId: aid, timestamp: ts, synthesized: true } }));
          window.dispatchEvent(new CustomEvent('ABR_ALL_MOVES_DONE', { detail: { agentId: aid, timestamp: ts, synthesized: true } }));
        }
      }
    } catch {}

    // 显式导出：一局结束或统计完成时，立即 flush + 导出完整日志
    try {
      if (eventType === 'GAME_ENDED' || eventType === 'STATS_GAME_COMPLETE') {
        try { (require('../telemetry/ClientTrace') as any).default.exportNow('game_end'); } catch {}
      }
    } catch {}
  }

  // 🆕 Registry集成
  setGameRegistry(registry: any): void {
    this.gameRegistry = registry;
    try {
      // 将 Registry 的 currentTurn 暴露给 ClientTrace
      ClientTrace.setTurnSupplier(() => {
        try { return this.gameRegistry?.get?.('currentTurn'); } catch { return undefined; }
      });
    } catch {}
  }

  hasGameRegistry(): boolean {
    return this.gameRegistry !== null;
  }

  updateGameState(key: string, value: any): void {
    if (this.gameRegistry) {
      // 保存历史记录
      this.saveToHistory(key, value);
      
      // 更新Registry
      this.gameRegistry.set(key, value);
      
      // 触发监听器
      this.triggerRegistryChangeListeners(key, value);
    }
  }

  // 🆕 Mock模式
  enableMockMode(): void {
    this.mockMode = true;
  }

  disableMockMode(): void {
    this.mockMode = false;
  }

  isMockMode(): boolean {
    return this.mockMode;
  }

  triggerMockEvent(eventType: string, data: any): void {
    if (this.mockMode) {
      this.triggerGameEvent(eventType, data);
    }
  }

  // 🆕 游戏状态自动管理
  private autoUpdateRegistryState(eventType: string, data: any): void {
    if (!this.gameRegistry) return;

    switch (eventType) {
      case 'GAME_STARTED':
        this.gameRegistry.set('gamePhase', 'RUNNING');
        this.gameRegistry.set('gameStartTime', Date.now());
        break;
      case 'TURN_STARTED':
        if (data.turnNumber !== undefined) {
          this.gameRegistry.set('currentTurn', data.turnNumber);
        }
        break;
      case 'AGENT_ELIMINATED':
        if (data.remainingCount !== undefined) {
          this.gameRegistry.set('aliveAgents', data.remainingCount);
        }
        break;
      case 'GAME_ENDED':
        this.gameRegistry.set('gamePhase', 'ENDED');
        break;
    }
  }

  // 🆕 Registry增强功能实现 - Phase 3

  /**
   * 批量更新游戏状态
   */
  updateGameStateBatch(updates: { [key: string]: any }): void {
    if (!this.gameRegistry) return;
    
    Object.entries(updates).forEach(([key, value]) => {
      // 保存历史记录
      this.saveToHistory(key, value);
      
      // 更新Registry
      this.gameRegistry.set(key, value);
      
      // 触发监听器
      this.triggerRegistryChangeListeners(key, value);
    });
  }

  // ================= 可视化回放入口（由 BattleScene/动画层注入 Promise 适配器） =================
  configurePlayback(handlers: VizHandlers, cfg?: Partial<{ watchdogMs: number; resourceMarkerMs: number; safezoneMarkerMs: number }>): void {
    this.playbackScheduler = new PlaybackScheduler(handlers, cfg);
    this.playbackEnabled = true;
    try {
      // 将播放模式写入 Registry，供 BattleScene/其他系统观测选择“状态-only”处理
      this.gameRegistry?.set('playbackEnabled', true);
    } catch {}
    // 安装粘性就绪 Window 钩子（ABR_* → ReadinessStore），支持“状态门”
    try { (require('../playback/ReadinessStore') as any).default.installWindowHooks(); } catch {}
  }

  disablePlayback(): void {
    this.playbackEnabled = false;
    this.playbackScheduler = null;
    try { this.gameRegistry?.set('playbackEnabled', false); } catch {}
  }

  // 只读：供外部查询是否启用了可视化回放队列
  isPlaybackEnabled(): boolean { return !!this.playbackEnabled; }

  // 将后端事件映射为 PlaybackScheduler 的事件类型（仅可视化相关）
  private mapToVizEvent(eventType: string, payload: any): VizEvent | null {
    try { if (payload && payload.__viz_internal) return null; } catch {}
    switch (eventType) {
      // --- TURN / MOVE ---
      case 'TURN_STARTED':
      case 'AGENT_TURN_STARTED':
        return { type: 'TURN_STARTED', payload };
      case 'TURN_ENDED':
      case 'AGENT_TURN_ENDED':
        return { type: 'TURN_ENDED', payload };
      case 'AGENT_MOVED': {
        const path = payload?.path || payload?.positions || payload?.pathNodes || [];
        return { type: 'ACTION_MOVE', payload: { ...payload, path } };
      }
      // --- COMBAT ---
      case 'COMBAT_STARTED': {
        const attackerId = payload?.attackerId ?? payload?.attacker;
        const defenderId = payload?.defenderId ?? payload?.defender;
        const enriched = { ...payload };
        if (attackerId && !enriched.attackerColor) {
          const color = this.lookupAgentColor(attackerId);
          if (color) enriched.attackerColor = color;
        }
        if (defenderId && !enriched.defenderColor) {
          const color = this.lookupAgentColor(defenderId);
          if (color) enriched.defenderColor = color;
        }
        this.logCombatColorMapping('mapToVizEvent', attackerId, defenderId, enriched);
        return { type: 'COMBAT_SIGNAL_STARTED', payload: enriched };
      }
      case 'COMBAT_DAMAGE_DEALT': {
        const p = payload || {};
        const attackerId = p.attackerId || p.attacker;
        const defenderId = p.defenderId || p.defender;
        const enriched = { ...p };
        if (attackerId && !enriched.attackerColor) {
          const color = this.lookupAgentColor(attackerId);
          if (color) enriched.attackerColor = color;
        }
        if (defenderId && !enriched.defenderColor) {
          const color = this.lookupAgentColor(defenderId);
          if (color) enriched.defenderColor = color;
        }
        return { type: 'COMBAT_SIGNAL_ATTACK', payload: { attackerId, defenderId, damage: p.damage, ...enriched } };
      }
      case 'COMBAT_ATTACK_MISSED': {
        const p = payload || {};
        const attackerId = p.attackerId || p.attacker;
        const defenderId = p.defenderId || p.defender;
        const enriched = { ...p };
        if (attackerId && !enriched.attackerColor) {
          const color = this.lookupAgentColor(attackerId);
          if (color) enriched.attackerColor = color;
        }
        if (defenderId && !enriched.defenderColor) {
          const color = this.lookupAgentColor(defenderId);
          if (color) enriched.defenderColor = color;
        }
        return { type: 'COMBAT_SIGNAL_MISS', payload: enriched };
      }
      case 'COMBAT_ENDED':
        return { type: 'COMBAT_SIGNAL_ENDED', payload };
      // --- HP / ELIMINATION ---
      case 'AGENT_HEALTH_CHANGED': {
        const source = String(payload?.changeSource || '').toLowerCase();
        if (source === 'combat') {
          return { type: 'COMBAT_SIGNAL_HP', payload };
        }
        return { type: 'AGENT_HEALTH_CHANGED', payload };
      }
      case 'AGENT_ELIMINATED':
        return { type: 'AGENT_ELIMINATED', payload };
      case 'WEAPON_EQUIPPED':
        return { type: 'WEAPON_EQUIPPED', payload };
      // --- RESOURCES ---
      case 'RESOURCE_SPAWNED':
        return { type: 'RESOURCE_SPAWNED', payload };
      case 'RESOURCE_COLLECTED': {
        const aid = payload?.agentId;
        if (!aid || aid === 'system') {
          try { ClientTrace.log('receive','ws-bridge','RESOURCE_COLLECTED_DROPPED', { details: { reason: (!aid ? 'missingAgentId' : 'system'), itemId: payload?.itemId || payload?.resourceId } }); } catch {}
          return null;
        }
        return { type: 'RESOURCE_COLLECTED', payload };
      }
      // --- SAFEZONE ---
      case 'SAFEZONE_WARNING':
      case 'SAFEZONE_CONTRACTION_STARTED':
      case 'SAFEZONE_CONTRACTION_COMPLETED':
      case 'SAFEZONE_UPDATED':
        return { type: eventType, payload };
      default:
        return null;
    }
  }

  /**
   * 监听Registry状态变化
   */
  onRegistryChange(key: string, callback: (value: any) => void): void {
    if (!this.registryChangeListeners.has(key)) {
      this.registryChangeListeners.set(key, []);
    }
    this.registryChangeListeners.get(key)!.push(callback);
  }

  /**
   * 移除Registry状态监听器
   */
  offRegistryChange(key: string, callback: (value: any) => void): void {
    const listeners = this.registryChangeListeners.get(key);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
        if (listeners.length === 0) {
          this.registryChangeListeners.delete(key);
        }
      }
    }
  }

  /**
   * 创建Registry状态快照
   */
  createRegistrySnapshot(): { [key: string]: any } {
    if (!this.gameRegistry) return {};
    
    const snapshot: { [key: string]: any } = {};
    
    // 假设Registry有一个获取所有键值对的方法
    // 这里使用一个简化的实现，在实际使用中可能需要根据具体的Registry实现调整
    try {
      // 尝试从registryHistory获取当前所有键
      for (const key of this.registryHistory.keys()) {
        const value = this.gameRegistry.get(key);
        if (value !== undefined) {
          snapshot[key] = value;
        }
      }
    } catch (error) {
      console.warn('Error creating registry snapshot:', error);
    }
    
    this.registrySnapshot = new Map(Object.entries(snapshot));
    return snapshot;
  }

  /**
   * 恢复Registry状态快照
   */
  restoreRegistrySnapshot(snapshot: { [key: string]: any }): void {
    if (!this.gameRegistry) return;
    
    Object.entries(snapshot).forEach(([key, value]) => {
      this.gameRegistry.set(key, value);
      // 恢复快照时不保存历史记录，避免污染历史
      this.triggerRegistryChangeListeners(key, value);
    });
  }

  /**
   * 获取Registry调试信息
   */
  getRegistryDebugInfo(): {
    totalKeys: number;
    registryConnected: boolean;
    stateKeys: string[];
    stateValues: { [key: string]: any };
  } {
    const stateKeys = Array.from(this.registryHistory.keys());
    const stateValues: { [key: string]: any } = {};
    
    // 获取当前状态值
    stateKeys.forEach(key => {
      if (this.gameRegistry) {
        stateValues[key] = this.gameRegistry.get(key);
      }
    });
    
    return {
      totalKeys: stateKeys.length,
      registryConnected: this.gameRegistry !== null,
      stateKeys,
      stateValues
    };
  }

  /**
   * 清理Registry状态
   */
  cleanupRegistryState(keysToRemove?: string[]): {
    removedKeys: string[];
    removedCount: number;
  } {
    const removedKeys: string[] = [];
    
    if (keysToRemove) {
      // 清理指定的键
      keysToRemove.forEach(key => {
        if (this.gameRegistry && this.gameRegistry.get(key) !== undefined) {
          this.gameRegistry.set(key, undefined);
          removedKeys.push(key);
        }
        // 清理历史记录
        this.registryHistory.delete(key);
        // 清理监听器
        this.registryChangeListeners.delete(key);
      });
    } else {
      // 清理空值和无效值
      Array.from(this.registryHistory.keys()).forEach(key => {
        if (this.gameRegistry) {
          const value = this.gameRegistry.get(key);
          if (value === null || value === undefined || value === '') {
            this.gameRegistry.set(key, undefined);
            removedKeys.push(key);
            this.registryHistory.delete(key);
          }
        }
      });
    }
    
    return {
      removedKeys,
      removedCount: removedKeys.length
    };
  }

  private recordPlannedMoveSteps(agentId: string, steps: number): void {
    const clamped = Math.max(0, steps | 0);
    if (!clamped) return;
    this.plannedMoveStepsByAgent.set(agentId, { steps: clamped, recordedAt: Date.now() });
  }

  private consumePlannedMoveSteps(agentId: string): number {
    const entry = this.plannedMoveStepsByAgent.get(agentId);
    if (!entry) return 0;
    this.plannedMoveStepsByAgent.delete(agentId);
    return Math.max(0, entry.steps | 0);
  }

  private deriveStepsFromPathPlan(payload: any): number {
    if (!payload) return 0;
    if (typeof payload.pathLength === 'number') {
      return Math.max(0, (payload.pathLength | 0) - 1);
    }
    if (Array.isArray(payload.path)) {
      return Math.max(0, payload.path.length - 1);
    }
    if (Array.isArray(payload.plannedActions)) {
      // fallback: count MOVE actions if provided
      const moves = payload.plannedActions.filter((act: any) => act?.type === 'MOVE').length;
      return Math.max(0, moves);
    }
    return 0;
  }

  private normalizeEventType(eventType: string): string {
    return String(eventType || '').replace(/:/g, '_').toUpperCase();
  }

  private logProbe(eventType: string, detail: Record<string, any>): void {
    if (!PROBE_ENABLED) return;
    try {
      ClientTrace.log('probe', 'frontend', eventType, detail);
    } catch {}
  }

  private classifyEvent(eventType: string, payload: any): EventClassification {
    const norm = this.normalizeEventType(eventType);
    if (MOVE_EVENTS.has(norm)) return 'move';
    if (norm === 'COMBAT_SIGNAL_HP') return 'combat_hp';
    if (COMBAT_EVENTS.has(norm)) return 'combat';
    if (norm === 'AGENT_HEALTH_CHANGED') {
      const source = String(payload?.changeSource || '').toLowerCase();
      if (source === 'combat') return 'combat_hp';
      if (source === 'safezone') return 'other';
      if (this.activeCombatWindow) {
        const { attackerId, defenderId } = this.activeCombatWindow;
        if (payload?.agentId && (payload.agentId === attackerId || payload.agentId === defenderId)) {
          return 'combat_hp';
        }
      }
      return 'other';
    }
    if (META_EVENTS.has(norm)) {
      return 'meta';
    }
    return 'other';
  }

  private handleWindowPriming(eventType: string, payload: any, classification: EventClassification, timestamp: number): void {
    if (classification === 'move') {
      this.openMoveWindowIfNeeded(payload, timestamp);
      return;
    }
    if (classification === 'combat') {
      if (this.activeMoveWindow) {
        this.endMoveWindow();
      }
      this.handleCombatWindowEvent(eventType, payload, timestamp);
      return;
    }
    if (classification === 'combat_hp' && payload?.current !== undefined) {
      if (payload.current <= 0 && this.activeCombatWindow) {
        this.activeCombatWindow.hasDeath = true;
        this.recomputeCombatWindowEnd(this.activeCombatWindow);
      }
    }
  }

  private openMoveWindowIfNeeded(payload: any, timestamp: number): void {
    if (this.activeMoveWindow) return;
    const agentId = payload?.agentId ? String(payload.agentId) : undefined;
    if (!agentId) return;
    const turn = payload?.turnNumber ?? payload?.turn ?? 0;
    let steps = this.consumePlannedMoveSteps(agentId);
    if (steps <= 0) {
      const path = Array.isArray(payload?.path) ? payload.path : Array.isArray(payload?.positions) ? payload.positions : null;
      steps = path && path.length > 1 ? (path.length - 1) : MOVE_FALLBACK_PATH;
    }
    const duration = computeMovePlannedDuration(Math.max(1, steps));
    if (PROBE_ENABLED) {
      this.logProbe('OPEN_MOVE_WINDOW', { agentId, turn, steps, plannedMs: duration });
    }
    this.activeMoveWindow = {
      agentId,
      turn,
      endAt: timestamp + duration
    };
    if (this.moveWindowTimer) {
      try { clearTimeout(this.moveWindowTimer); } catch {}
      this.moveWindowTimer = null;
    }
    if (duration > 0) {
      this.moveWindowTimer = window.setTimeout(() => this.handleMoveWindowEnd(), duration);
    } else {
      this.handleMoveWindowEnd();
    }
    // 🔍 打点：移动窗口开启（预测时长 + 结束时间）
    try {
      ClientTrace.log('playback','playback','OPEN_MOVE_WINDOW', {
        agentId,
        turn,
        details: { steps, plannedMs: duration, startAt: timestamp, endAt: timestamp + duration }
      });
    } catch {}
  }

  private handleCombatWindowEvent(eventType: string, payload: any, timestamp: number): void {
    const combatId = payload?.combatId || payload?.cid;
    if (!combatId) {
      return;
    }
    const normRaw = this.normalizeEventType(eventType);
    const norm = this.normalizeCombatSignal(normRaw);
    if (norm === 'COMBAT_SIGNAL_HP') {
      if (payload?.current !== undefined && payload.current <= 0 && this.activeCombatWindow && this.activeCombatWindow.combatId === combatId) {
        this.activeCombatWindow.hasDeath = true;
        this.recomputeCombatWindowEnd(this.activeCombatWindow);
      }
      return;
    }

    if (norm === 'COMBAT_STARTED') {
      if (this.activeCombatWindow && this.activeCombatWindow.combatId === combatId) {
        return;
      }
      this.activeCombatWindow = {
        combatId,
        startAt: timestamp,
        attackCount: 0,
        missCount: 0,
        hasDeath: false,
        attackerId: payload?.attackerId,
        defenderId: payload?.defenderId
      };
      this.recomputeCombatWindowEnd(this.activeCombatWindow);
      try {
        ClientTrace.log('playback','playback','OPEN_COMBAT_WINDOW', {
          details: {
            combatId,
            attackerId: this.activeCombatWindow.attackerId,
            defenderId: this.activeCombatWindow.defenderId,
            startAt: timestamp,
            plannedEndAt: this.activeCombatWindow.endAt
          }
        });
      } catch {}
      return;
    }

    const windowState = this.activeCombatWindow;
    if (!windowState || windowState.combatId !== combatId) {
      return;
    }

    if (norm === 'COMBAT_DAMAGE_DEALT' || norm === 'COMBAT_CRITICAL_HIT') {
      windowState.attackCount += 1;
      if (this.didPayloadCauseDeath(payload)) {
        windowState.hasDeath = true;
      }
      this.recomputeCombatWindowEnd(windowState);
      return;
    }
    if (norm === 'COMBAT_ATTACK_MISSED') {
      windowState.missCount += 1;
      this.recomputeCombatWindowEnd(windowState);
      return;
    }
    if (norm === 'COMBAT_ENDED') {
      if (payload?.loser) {
        windowState.hasDeath = true;
      }
      this.recomputeCombatWindowEnd(windowState);
    }
  }

  private normalizeCombatSignal(type: string): string {
    const norm = type.toUpperCase();
    if (!norm.startsWith('COMBAT_SIGNAL_')) return norm;
    const suffix = norm.substring('COMBAT_SIGNAL_'.length);
    switch (suffix) {
      case 'STARTED': return 'COMBAT_STARTED';
      case 'ATTACK': return 'COMBAT_DAMAGE_DEALT';
      case 'MISS': return 'COMBAT_ATTACK_MISSED';
      case 'ENDED': return 'COMBAT_ENDED';
      case 'HP': return 'COMBAT_SIGNAL_HP';
      default: return norm;
    }
  }

  private didPayloadCauseDeath(payload: any): boolean {
    if (!payload) return false;
    const segments = [
      payload?.defenderRemainingHealth,
      payload?.defenderHealth,
      payload?.remainingHealth
    ];
    return segments.some(seg => seg && typeof seg.current === 'number' && seg.current <= 0);
  }

  private recomputeCombatWindowEnd(windowState: CombatWindowState): void {
    const base = computeCombatPlannedDuration(windowState.attackCount, windowState.missCount, windowState.hasDeath);
    const overhead = COMBAT_TIMING_CONSTANTS.CURTAIN_CLOSE_MS;
    const mapDeath = windowState.hasDeath ? COMBAT_TIMING_CONSTANTS.MAP_DEATH_CLEANUP_MS : 0;
    windowState.endAt = windowState.startAt + base + overhead + mapDeath;
    // 🔍 打点：战斗窗口时长更新（用于排查收口条件）
    try {
      ClientTrace.log('playback','playback','COMBAT_WINDOW_UPDATE', {
        details: {
          combatId: windowState.combatId,
          attacks: windowState.attackCount,
          misses: windowState.missCount,
          hasDeath: windowState.hasDeath,
          startAt: windowState.startAt,
          endAt: windowState.endAt,
          baseMs: base,
          overhead,
          mapDeath
        }
      });
    } catch {}
  }

  private computeGateDelayIfNeeded(eventType: string, payload: any, classification: EventClassification, timestamp: number): number {
    if (classification === 'meta') return 0;
    // HP（combat_hp）：
    // - 若存在移动窗口：等移动窗口结束（与战斗信号同源，避免HP越过移动收口）
    // - 若尚未建立战斗窗口：短暂等待，让 COMBAT_STARTED 先入场并建立窗口
    if (classification === 'combat_hp') {
      if (this.activeMoveWindow) {
        const waitMs = Math.max(0, this.activeMoveWindow.endAt - timestamp);
        if (waitMs > 0) {
          try { ClientTrace.log('playback','playback','GATE_HP_MOVE_WINDOW', { details: { plannedMs: waitMs } }); } catch {}
        }
        return waitMs;
      }
      if (!this.activeCombatWindow) {
        const PRESTART_MS = 300;
        try { ClientTrace.log('playback','playback','GATE_HP_PRESTART', { details: { plannedMs: PRESTART_MS } }); } catch {}
        return PRESTART_MS;
      }
      return 0;
    }
    // 移动窗口：对非移动事件施加单次闸门，但不再向队列塞 WAIT 任务，改为延迟回调当前事件
    if (this.activeMoveWindow && classification !== 'move' && classification !== 'combat' && classification !== 'combat_hp') {
      const waitMs = Math.max(0, this.activeMoveWindow.endAt - timestamp);
      if (waitMs > 0) {
        try { ClientTrace.log('playback','playback','GATE_MOVE_WINDOW', { details: { plannedMs: waitMs } }); } catch {}
      }
      return waitMs;
    }
    // 战斗窗口：对非战斗事件（含 combat_hp 之外）施加单次闸门
    if (this.activeCombatWindow && classification !== 'combat' && classification !== 'combat_hp') {
      const endAt = this.activeCombatWindow.endAt ?? (this.activeCombatWindow.startAt + computeCombatPlannedDuration(0, 0, false));
      const waitMs = Math.max(0, endAt - timestamp);
      if (waitMs > 0) {
        try { ClientTrace.log('playback','playback','GATE_COMBAT_WINDOW', { details: { combatId: this.activeCombatWindow.combatId, plannedMs: waitMs } }); } catch {}
      }
      return waitMs;
    }
    return 0;
  }

  private updateDamageSource(eventType: string, payload: any, classification: EventClassification, timestamp: number): void {
    const norm = this.normalizeEventType(eventType);
    const combatNorm = this.normalizeCombatSignal(norm);
    if (combatNorm === 'COMBAT_DAMAGE_DEALT' || combatNorm === 'COMBAT_CRITICAL_HIT') {
      const defender = payload?.defenderId || payload?.defender;
      if (defender) {
        this.lastDamageSource.set(String(defender), { source: 'combat', timestamp });
      }
    } else if (SAFEZONE_EVENTS.has(norm)) {
      const agentId = payload?.agentId;
      if (agentId) {
        this.lastDamageSource.set(String(agentId), { source: 'safezone', timestamp });
      }
    } else if (norm === 'AGENT_HEALTH_CHANGED' || combatNorm === 'COMBAT_SIGNAL_HP') {
      const source = String(payload?.changeSource || '').toLowerCase();
      const agentId = payload?.agentId;
      if (agentId && (source === 'combat' || source === 'safezone' || source === 'environment')) {
        this.lastDamageSource.set(String(agentId), { source: source as DamageSourceKind, timestamp });
      }
    }

    if (norm === 'AGENT_ELIMINATED' && payload?.agentId && !payload.deathCause) {
      const cause = this.determineDeathCause(payload, timestamp);
      if (cause !== 'unknown') {
        payload.deathCause = cause;
      }
    }
  }

  private determineDeathCause(payload: any, timestamp: number): DamageSourceKind | 'unknown' {
    const rawCause = String(payload?.cause || payload?.changeSource || '').toLowerCase();
    if (rawCause === 'combat' || rawCause === 'safezone' || rawCause === 'environment') {
      return rawCause as DamageSourceKind;
    }
    const agentId = payload?.agentId ? String(payload.agentId) : undefined;
    if (!agentId) return 'unknown';
    if (this.activeCombatWindow && (agentId === this.activeCombatWindow.attackerId || agentId === this.activeCombatWindow.defenderId)) {
      return 'combat';
    }
    const entry = this.lastDamageSource.get(agentId);
    if (entry && (timestamp - entry.timestamp) <= DAMAGE_SOURCE_TTL_MS) {
      return entry.source;
    }
    return 'unknown';
  }

  private bufferCombatEventIfNeeded(eventType: string, payload: any): boolean {
    if (!this.activeMoveWindow) return false;
    const combatId = payload?.combatId || payload?.cid;
    if (!combatId) return false;
    const bucket = this.pendingCombatBuffers.get(combatId) || { events: [], flushAt: this.activeMoveWindow.endAt };
    bucket.flushAt = this.activeMoveWindow.endAt;
    bucket.events.push({ type: eventType, payload });
    this.pendingCombatBuffers.set(combatId, bucket);
    try {
      ClientTrace.log('playback','playback','PREDICT_COMBAT_BUFFERED', {
        details: {
          combatId,
          plannedFlushAt: bucket.flushAt,
          count: bucket.events.length
        }
      });
    } catch {}
    return true;
  }

  private flushBufferedCombatEvents(): void {
    if (!this.pendingCombatBuffers.size) return;
    for (const [combatId, bucket] of this.pendingCombatBuffers.entries()) {
      this.pendingCombatBuffers.delete(combatId);
      const sorted = bucket.events.slice().sort((a, b) => {
        const sa = typeof a.payload?.__seq === 'number' ? a.payload.__seq : Number.MAX_SAFE_INTEGER;
        const sb = typeof b.payload?.__seq === 'number' ? b.payload.__seq : Number.MAX_SAFE_INTEGER;
        return sa - sb;
      });
      try {
        ClientTrace.log('playback','playback','PREDICT_COMBAT_FLUSH', {
          details: {
            combatId,
            count: sorted.length
          }
        });
      } catch {}
      for (const entry of sorted) {
        try {
          this.triggerGameEvent(entry.type, entry.payload, { skipGate: true });
        } catch (e) {
          console.warn('Buffered combat event dispatch failed:', e);
        }
      }
    }
  }

  private handleMoveWindowEnd(): void {
    if (this.moveWindowTimer) {
      try { clearTimeout(this.moveWindowTimer); } catch {}
      this.moveWindowTimer = null;
    }
    if (!this.activeMoveWindow) {
      this.flushBufferedCombatEvents();
      return;
    }
    this.activeMoveWindow = null;
    this.flushBufferedCombatEvents();
  }

  private endMoveWindow(): void {
    if (this.moveWindowTimer) {
      try { clearTimeout(this.moveWindowTimer); } catch {}
      this.moveWindowTimer = null;
    }
    if (!this.activeMoveWindow) {
      this.flushBufferedCombatEvents();
      return;
    }
    this.activeMoveWindow = null;
    this.flushBufferedCombatEvents();
  }

  private captureAgentColors(eventType: string, data: any): void {
    const record = (id?: string, color?: string) => {
      if (!id) return;
      const normalized = this.normalizeAgentColor(color);
      if (!normalized) return;
      this.agentColorById.set(String(id), normalized);
    };

    switch (eventType) {
      case 'SYSTEM_STATE_SYNC': {
        const candidates: any[] = [];
        if (Array.isArray(data?.agents)) candidates.push(...data.agents);
        if (Array.isArray(data?.state?.agents)) candidates.push(...data.state.agents);
        if (Array.isArray(data?.snapshot?.agents)) candidates.push(...data.snapshot.agents);
        candidates.forEach((agent: any) => {
          const agentId = agent?.id ?? agent?.agentId ?? agent?.agent?.id;
          const color = agent?.color ?? agent?.slimeType ?? agent?.displayColor;
          record(agentId, color);
        });
        break;
      }
      case 'AGENT_SPAWNED': {
        const agent = data?.agent ?? data;
        const agentId = agent?.id ?? agent?.agentId;
        const color = agent?.color ?? agent?.slimeType;
        record(agentId, color);
        break;
      }
      case 'AGENT_UPDATED':
      case 'AGENT_STATE_UPDATED':
      case 'AGENT_INFO_UPDATED': {
        const agentId = data?.agentId ?? data?.id;
        const color = data?.color ?? data?.slimeType;
        record(agentId, color);
        break;
      }
      default:
        break;
    }
  }

  private logCombatColorMapping(stage: string, attackerId?: string, defenderId?: string, payload?: any): void {
    try {
      if (typeof console === 'undefined') return;
      const attackerCache = attackerId ? this.lookupAgentColor(attackerId) : undefined;
      const defenderCache = defenderId ? this.lookupAgentColor(defenderId) : undefined;
      console.debug('[CombatColor]', stage, {
        attackerId,
        payloadAttackerColor: payload?.attackerColor,
        cachedAttackerColor: attackerCache,
        defenderId,
        payloadDefenderColor: payload?.defenderColor,
        cachedDefenderColor: defenderCache
      });
    } catch {}
  }

  private normalizeAgentColor(color?: any): string | undefined {
    if (color === null || color === undefined) return undefined;
    const str = String(color).trim();
    return str.length ? str : undefined;
  }

  private lookupAgentColor(agentId?: string): string | undefined {
    if (!agentId) return undefined;
    return this.agentColorById.get(String(agentId));
  }

  /**
   * 获取Registry状态历史
   */
  getRegistryHistory(key: string): { value: any; timestamp: number }[] {
    return this.registryHistory.get(key) || [];
  }

  /**
   * 私有辅助方法：保存历史记录
   */
  private saveToHistory(key: string, value: any): void {
    if (!this.registryHistory.has(key)) {
      this.registryHistory.set(key, []);
    }
    
    const history = this.registryHistory.get(key)!;
    history.push({
      value,
      timestamp: Date.now()
    });
    
    // 限制历史记录长度（保留最近100条）
    if (history.length > 100) {
      history.splice(0, history.length - 100);
    }
  }

  /**
   * 私有辅助方法：触发Registry变化监听器
   */
  private triggerRegistryChangeListeners(key: string, value: any): void {
    const listeners = this.registryChangeListeners.get(key);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(value);
        } catch (error) {
          console.error('Error in registry change listener:', error);
        }
      });
    }
  }
}

// Export a singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;
